import { useState } from "react";
import { asset } from "../lib/api";
import { Play, Newspaper, Trophy } from "lucide-react";

export const VendorStatement = ({ text }) => (
  <section className="border-y border-amber-glow/20 bg-obsidian-800" data-testid="incognito-vendor">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-[auto,1fr] gap-10 items-start">
      <div className="overline-label text-amber-glow md:pt-2 md:w-40">Not your<br className="hidden md:block" /> wedding photographer</div>
      <p className="font-serif text-2xl lg:text-3xl font-light leading-snug whitespace-pre-line">{text}</p>
    </div>
  </section>
);

const Pair = ({ p, i }) => (
  <figure data-testid={`story-pair-${i}`} className="grid grid-cols-2 gap-2 sm:gap-3">
    {[["What the guests saw", p.guest_url, "text-neutral-400"], ["What I caught", p.tele_url, "text-amber-glow"]].map(([label, src, color]) => (
      <div key={label} className="relative bg-obsidian-800 rounded-md overflow-hidden flex items-center justify-center aspect-[4/5]">
        <img src={asset(src)} alt={label} loading="lazy" className="max-w-full max-h-full object-contain" />
        <span className={`absolute top-2 left-2 overline-label whitespace-nowrap text-[9px] sm:text-xs bg-black/70 rounded px-2 py-1 ${color}`}>{label}</span>
      </div>
    ))}
    {p.caption && <figcaption className="col-span-2 text-sm text-neutral-400 italic">{p.caption}</figcaption>}
  </figure>
);

export const MomentStories = ({ pairs, intro }) => {
  const [showAll, setShowAll] = useState(false);
  if (!pairs.length) return null;
  const list = showAll ? pairs : pairs.slice(0, 4);
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="incognito-stories">
      <div className="overline-label mb-3">Real-moment stories</div>
      <h2 className="font-serif text-4xl lg:text-5xl font-light mb-4">Same day. A different story.</h2>
      <p className="text-neutral-400 max-w-2xl mb-12">{intro}</p>
      <div className="grid gap-12 lg:grid-cols-2">
        {list.map((p, i) => <Pair key={p.id || i} p={p} i={i} />)}
      </div>
      {pairs.length > 4 && !showAll && (
        <button data-testid="stories-show-all" onClick={() => setShowAll(true)}
          className="mt-12 border border-white/20 rounded-full px-6 py-2.5 text-sm hover:border-amber-glow hover:text-amber-glow transition-colors">
          See {pairs.length - 4} more stories
        </button>
      )}
    </section>
  );
};

const Phone = ({ children, testid }) => (
  <div data-testid={testid} className="relative shrink-0 w-[200px] sm:w-[230px] aspect-[9/19] rounded-[2.2rem] border-[6px] border-neutral-800 bg-black overflow-hidden shadow-2xl">
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-neutral-900 z-10" />
    {children}
  </div>
);

export const SocialClips = ({ clips, posters, intro }) => (
  <section className="border-y border-white/8 bg-obsidian-800 overflow-hidden" data-testid="incognito-clips">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-[1fr,auto] gap-12 items-center">
      <div>
        <div className="overline-label mb-3">Ready to post</div>
        <h2 className="font-serif text-4xl lg:text-5xl font-light mb-4">Social clips, the same day</h2>
        <p className="text-neutral-400 max-w-md">{intro}</p>
      </div>
      <div className="flex gap-5 overflow-x-auto pb-2">
        {clips.length
          ? clips.map((c, i) => (
            <Phone key={c.id} testid={`clip-${i}`}>
              <video src={asset(c.url)} className="w-full h-full object-cover" autoPlay muted loop playsInline />
            </Phone>
          ))
          : posters.slice(0, 3).map((src, i) => (
            <Phone key={i} testid={`clip-placeholder-${i}`}>
              <img src={asset(src)} alt="" className="w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/90">
                <span className="rounded-full bg-black/60 p-3"><Play size={20} fill="currentColor" /></span>
                <span className="overline-label bg-black/75 rounded-full px-3 py-1">Clip coming soon</span>
              </div>
            </Phone>
          ))}
      </div>
    </div>
  </section>
);

export const WhoYoureHiring = ({ intro, list, press }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="incognito-hiring">
    <div className="overline-label mb-3">Who you're hiring</div>
    <h2 className="font-serif text-4xl lg:text-5xl font-light mb-6">The same eye that covered</h2>
    <p className="text-neutral-400 max-w-2xl mb-12">{intro}</p>
    <div className={`grid gap-12 ${press.length ? "lg:grid-cols-2" : ""}`}>
      <ul className="divide-y divide-white/8 border-y border-white/8">
        {list.split("\n").filter((l) => l.trim()).map((l, i) => (
          <li key={i} data-testid={`hire-credit-${i}`} className="py-4 flex items-center gap-4 font-serif text-2xl lg:text-3xl font-light">
            <Trophy size={18} className="text-amber-glow shrink-0" /> {l}
          </li>
        ))}
      </ul>
      {press.map((p) => (
        <figure key={p.id} data-testid="press-feature" className="border border-white/10 rounded-lg bg-obsidian-800 p-4">
          <img src={asset(p.url)} alt={p.title || "Press feature"} className="w-full rounded object-contain max-h-[620px]" />
          <figcaption className="mt-4 flex items-start gap-3">
            <Newspaper size={18} className="text-amber-glow mt-1 shrink-0" />
            <div>
              {p.outlet && <div className="overline-label text-amber-glow">{p.outlet}</div>}
              {p.title && <div className="font-serif text-xl font-light mt-1">{p.title}</div>}
            </div>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);
